import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  OccupiedSlotsHint,
  useOccupiedSlots,
} from "@/components/common/OccupiedSlotsHint";
import { LockedDateField } from "@/components/common/LockedDateField";
import { RHFWorkHoursTimePicker } from "@/components/ui/rhf-work-hours-time-picker";
import { DurationChip } from "@/components/ui/duration-chip";
import { hasTimeOverlap } from "@/lib/time-utils";
import {
  getOtherTaskTypes,
  getOtherTasksConfig,
} from "@/lib/other-tasks-config";
import {
  insertOtherTaskForDept,
  updateOtherTaskForDept,
  type OtherTaskInput,
  type OtherTaskRow,
} from "@/lib/other-tasks";

const baseSchema = z.object({
  start_time: z.string().min(1, "Required"),
  end_time: z.string().min(1, "Required"),
  task_type: z.string().min(1, "Select a type"),
  notes: z.string().min(1, "Required").max(1000),
});

type FormValues = z.infer<typeof baseSchema>;

interface OtherTaskFormProps {
  department: string;
  userId: string | undefined;
  editingTask?: OtherTaskRow | null;
  onSuccess: () => void;
  onCancel: () => void;
}

function buildDefaults(editing?: OtherTaskRow | null): FormValues {
  if (editing) {
    return {
      start_time: editing.start_time.slice(0, 5),
      end_time: editing.end_time.slice(0, 5),
      task_type: editing.task_type,
      notes: editing.notes,
    };
  }
  return { start_time: "", end_time: "", task_type: "", notes: "" };
}

export function OtherTaskForm({
  department,
  userId,
  editingTask,
  onSuccess,
  onCancel,
}: OtherTaskFormProps) {
  const qc = useQueryClient();
  const config = getOtherTasksConfig(department);
  const typeOptions = useMemo(() => getOtherTaskTypes(department), [department]);

  // Restrict allowed values to the dept's options (still accept legacy values).
  const schema = useMemo(() => {
    const allowed = typeOptions.map((t) => t.value);
    return baseSchema.extend({
      task_type: z
        .string()
        .min(1, "Select a type")
        .refine(
          (v) => allowed.includes(v) || (!!editingTask && v === editingTask.task_type),
          "Select a valid type",
        ),
    });
  }, [typeOptions, editingTask]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema as never),
    defaultValues: buildDefaults(editingTask),
  });
  const errs = form.formState.errors;

  const { data: occupiedSlots = [] } = useOccupiedSlots(userId);

  const mut = useMutation({
    mutationFn: async (v: FormValues) => {
      const payload: OtherTaskInput = {
        task_type: v.task_type,
        start_time: v.start_time,
        end_time: v.end_time,
        notes: v.notes,
      };
      if (editingTask) {
        await updateOtherTaskForDept(editingTask.id, department, payload);
      } else {
        if (!userId) throw new Error("Not signed in");
        await insertOtherTaskForDept(userId, department, payload);
      }
    },
    onSuccess: () => {
      toast.success(editingTask ? "Task updated" : "Task submitted");
      if (config) qc.invalidateQueries({ queryKey: [config.queryKey] });
      onSuccess();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onSubmit = (v: FormValues) => {
    if (hasTimeOverlap(v.start_time, v.end_time, occupiedSlots, editingTask?.id)) {
      toast.error(
        "This time slot is already occupied. Choose a different start or end time.",
      );
      return;
    }
    mut.mutate(v);
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-3">
      <LockedDateField
        editingDate={editingTask?.task_date}
        createdAt={editingTask?.created_at}
      />

      <div className="grid grid-cols-2 gap-3">
        <RHFWorkHoursTimePicker
          control={form.control}
          name="start_time"
          label="CHOOSE TIMING"
          required
          error={errs.start_time?.message}
        />
        <RHFWorkHoursTimePicker
          control={form.control}
          name="end_time"
          label=""
          required
          minTime={form.watch("start_time")}
          error={errs.end_time?.message}
        />
      </div>
      <OccupiedSlotsHint />
      <DurationChip
        startTime={form.watch("start_time")}
        endTime={form.watch("end_time")}
      />

      <Field label="TASK TYPE" error={errs.task_type?.message}>
        <Select
          value={form.watch("task_type") ?? ""}
          onValueChange={(v) =>
            form.setValue("task_type", v, { shouldValidate: true })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {typeOptions.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field label="NOTES" error={errs.notes?.message}>
        <Textarea
          rows={4}
          maxLength={1000}
          placeholder="Describe the task or issue in detail..."
          {...form.register("notes")}
        />
      </Field>

      <div className="mt-2 flex items-center justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? "Submitting..." : "Submit Task"}
          <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
        {label}
      </Label>
      {children}
      {error && <p className="text-[11px] text-destructive">{error}</p>}
    </div>
  );
}
